/**
 * PATH: packages/email-hub/src/feature/components/FolderEmailRow.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENTS-FOLDEREMAILROW-001
 * PURPOSE: ONE rich folder row — the SAME <EmailRow> body + per-row
 *          toolkit the Inbox renders (avatar + category dot + AI tag +
 *          category chip + mailbox-attribution pill + relation chip +
 *          board/job FRAME + lane allocation chip + per-row Bin + "Mark
 *          junk") — shared by every non-Inbox folder shell (Sent /
 *          Archived / Starred / Binned) so they read AND behave
 *          identically to the Inbox.
 * WHY:     Operator 2026-06-17: "big difference in layout and
 *          functionality gap" — the first pass only stamped the AI tag +
 *          category chip, so the folders still lacked the Inbox's
 *          mailbox pill, board/job framing, lane chip, per-row Bin and
 *          "Mark junk". This wires the FULL Inbox row model (computed by
 *          the SAME pure resolvers + the SAME @quotech/kanban-host API)
 *          so there is ONE row body, not a per-folder fork. Folder
 *          semantics differ only where they MUST: Sent shows the
 *          recipient + no self-junk; Binned swaps Bin for "Not junk".
 * FLOW:
 *   ┌───────┐   ┌──────────────────────────────────────────┐   ┌──────────┐
 *   │ Email │ → │ resolveCardAiState · getCategoryConfig ·  │ → │ <EmailRow>│
 *   └───────┘   │ frame colours · mailbox/relation chips ·  │   └──────────┘
 *               │ kanban AllocationChip (same as the Inbox) │
 *               └──────────────────────────────────────────┘
 * DEPENDENCIES:
 *   - @quotech/ui EmailRow: the universal presentational row.
 *   - @quotech/kanban-host: AllocationChip + org-scoped api (lane chip).
 *   - lib/cardAiState · lib/categoryConfig · lib/colorPalettes: pure
 *     resolvers, identical to the Inbox.
 *   - components/{CardAiTag,CardVisibilityBadge,CategoryChip,
 *     CardJunkControl,RowAttributionChips}: shared row slots.
 * RELATED:
 *   - components/EmailFolderShell.tsx: passes (email, helpers) → renderRow.
 *   - pages/{Sent,Archived,Starred,Trash}Page.tsx: callers.
 *   - pages/InboxPage.tsx: the parity reference for every slot below.
 */
import { useMemo, useState, type MouseEvent, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { EmailRow, formatRecipientList } from '@quotech/ui'
import {
  AllocationChip,
  getSamKanbanApi,
  resolveKanbanOrgId,
} from '@quotech/kanban-host'

import { useApiAuth } from '../../_internal/components/ApiAuthGate'
import {
  getQuotechOrgIdOverride,
  getRuntimeQuotechOrgId,
} from '../../_internal/services/quotechApi'
import { useEmailActions, type Email } from '../hooks/useEmails'
import { useOrgCustomCategories, usePersonalCategories } from '../hooks/useEmailLabelPrefs'
import { getCategoryConfig } from '../lib/categoryConfig'
import { resolveCardAiState } from '../lib/cardAiState'
import { boardFrameColor, jobFrameColor, jobPillClassFor } from '../lib/colorPalettes'
import { buildKanbanBoardLink, buildKanbanCardLink } from '../lib/kanbanDeepLink'
import { CardAiTag } from './CardAiTag'
import { CardJunkControl } from './CardJunkControl'
import { CardVisibilityBadge } from './CardVisibilityBadge'
import { CategoryChip } from './CategoryChip'
import { MailboxAttributionChip, RelationChip, ResurfaceChip } from './RowAttributionChips'
import { WorkflowRoleChip } from './WorkflowRoleChip'

export interface FolderEmailRowProps {
  email: Email
  isSelected: boolean
  onSelect: () => void
  onContextMenu: (e: MouseEvent) => void
  /** Refetch the folder list after an in-row mutation (star, category,
   *  bin, junk). */
  onRefetch: () => void
  /** Outgoing folders (Sent) show the RECIPIENT, not the operator's own
   *  name — otherwise every row would be stamped with the viewer. Also
   *  suppresses the "Mark junk" control (junking your OWN sent mail would
   *  arm an auto-junk rule against your own address). */
  outgoing?: boolean
  /** The Binned folder. Swaps the per-row Bin (already binned) for the
   *  caller-supplied restore control and suppresses "Mark junk". */
  binned?: boolean
  /** Active folder search free-text — highlights the matched tokens in
   *  sender / subject / preview, exactly like the Inbox. */
  highlightQuery?: string
  /** Optional control pinned to the row's right edge (e.g. the Binned
   *  folder's "Not junk" restore). Rendered in EmailRow's always-visible
   *  trailing (junk) slot so it never clips off the card. Takes
   *  precedence over the auto "Mark junk" control. */
  trailingControl?: ReactNode
}

/**
 * FolderEmailRow(email, helpers) → a rich <EmailRow> matching the Inbox.
 * Pure metadata in, one row out; star / bin / category mutations call
 * onRefetch.
 */
export function FolderEmailRow({
  email,
  isSelected,
  onSelect,
  onContextMenu,
  onRefetch,
  outgoing = false,
  binned = false,
  highlightQuery,
  trailingControl,
}: FolderEmailRowProps) {
  const navigate = useNavigate()
  const personalCategories = usePersonalCategories()
  const orgCustomCategories = useOrgCustomCategories()
  const customCategories = [...personalCategories, ...orgCustomCategories]
  const { toggleStar, bulkAction, archive, unarchive, toggleRead } = useEmailActions()
  const { session } = useApiAuth()

  // Viewer identity — drives the mailbox-attribution pill (suppress the
  // viewer's OWN mailbox) exactly as the Inbox does.
  const currentUserEmail = session?.user?.email ?? null
  const currentUserId = useMemo(() => {
    const raw = session?.user?.id
    if (typeof raw === 'number') return raw
    if (typeof raw === 'string') {
      const n = Number.parseInt(raw, 10)
      return Number.isFinite(n) ? n : null
    }
    return null
  }, [session?.user?.id])

  // ONE org-scoped Kanban api instance for the lane allocation chip —
  // same source the Inbox + the Kanban rail use, so a board pinned in
  // the rail is visible in the chip's reallocate menu.
  const kanbanApi = useMemo(() => getSamKanbanApi(), [])
  const kanbanOrgId =
    resolveKanbanOrgId(session, {
      runtimeId: getRuntimeQuotechOrgId(),
      buildtimeId: getQuotechOrgIdOverride(),
    }) ?? null

  // Same per-row classification the Inbox computes (see InboxPage row map).
  const catKey = email.category || 'uncategorized'
  const catCfg = getCategoryConfig(catKey, customCategories)
  const CatIcon = catCfg.icon
  const dotIsMuted = catKey === 'general' || catKey === 'uncategorized'
  // Suppress the inline pill on general / uncategorised rows (the leading
  // dot already carries the state), matching the Inbox's `showInlineChip`.
  const showInlineChip = !dotIsMuted
  const cardAi = resolveCardAiState({
    allocation_summary: email.allocation_summary,
    category: email.category,
    category_locked: email.category_locked,
    suggested_job: email.suggested_job
      ? { job_id: email.suggested_job.job_id }
      : null,
  })

  // Board-first workflow FRAME (board > linked-job fallback) — the same
  // resolution the Inbox uses, so an allocated email reads as a framed
  // card here too. The AI accept/reject (✓/✗) stays Inbox-only triage:
  // we render the frame + lane chip but pass no confirm/reject handlers,
  // so folder rows surface the allocation without the triage workflow.
  const as = email.allocation_summary
  const rowFramed = !!as || !!email.job_id
  const allocationFrame = as
    ? {
        kind: 'board' as const,
        color: boardFrameColor(as.board_color, as.board_id),
        label: as.parent_board_name
          ? `${as.parent_board_name} › ${as.board_name}`
          : as.board_name,
        title: as.parent_board_name
          ? `${as.parent_board_name} › ${as.board_name} · ${as.lane_name}`
          : `${as.board_name} · ${as.lane_name}`,
        pending: as.needs_review === true,
        // Click the board tag → open the Kanban board (same as the Inbox).
        href: buildKanbanBoardLink(as) ?? undefined,
      }
    : email.job_id
      ? {
          kind: 'job' as const,
          color: jobFrameColor(email.job_id),
          label: email.job_name || `Job #${email.job_id}`,
          title: email.job_name ? `Job · ${email.job_name}` : `Job #${email.job_id}`,
        }
      : null

  // Sent shows "To: <recipient>" so the operator's own name doesn't stamp
  // every row; incoming folders fall back to the sender (EmailRow default).
  const recipient = outgoing
    ? formatRecipientList(email.to_addresses, { max: 2 }) || undefined
    : undefined

  // Per-row Bin (soft-delete → Binned, recoverable). The PRIMARY one-click
  // gesture on the Inbox; mirrored here for every folder EXCEPT Binned
  // (already binned → the caller supplies a restore control instead).
  const [binning, setBinning] = useState(false)
  const handleBin = async () => {
    if (binning) return
    setBinning(true)
    try {
      await bulkAction([email.id], 'delete')
      onRefetch()
    } catch {
      /* hook surfaces its own toast; next poll/refetch restores state */
    } finally {
      setBinning(false)
    }
  }

  const handleToggleStar = async (e: MouseEvent) => {
    // The star sits inside the row's click target — stop propagation so
    // toggling a star doesn't also pop the preview open.
    e.preventDefault()
    e.stopPropagation()
    try {
      await toggleStar(email.id, email.is_starred)
      onRefetch()
    } catch {
      /* hook surfaces its own toast; next poll/refetch restores state */
    }
  }

  // Archive / Unarchive — durable sibling of Bin. When already archived,
  // the disposition bar swaps to a reload-arrow Unarchive (operator 2026-08-10).
  const [archiving, setArchiving] = useState(false)
  const [unarchiving, setUnarchiving] = useState(false)
  const handleArchive = async () => {
    if (archiving) return
    setArchiving(true)
    try {
      await archive(email.id, {
        folder: email.folder,
        isRead: email.is_read,
        isStarred: email.is_starred,
      })
      onRefetch()
    } catch {
      /* hook surfaces its own toast; next poll/refetch restores state */
    } finally {
      setArchiving(false)
    }
  }
  const handleUnarchive = async () => {
    if (unarchiving) return
    setUnarchiving(true)
    try {
      await unarchive(email.id, { isRead: email.is_read })
      onRefetch()
    } catch {
      /* hook surfaces its own toast; next poll/refetch restores state */
    } finally {
      setUnarchiving(false)
    }
  }

  // Restore from Bin — same reload-arrow pattern as Unarchive. TrashPage also
  // pins a trailingControl; the disposition bar gets the same verb so the
  // hover/touch footer matches Archive's restore affordance.
  const [restoring, setRestoring] = useState(false)
  const handleRestore = async () => {
    if (restoring) return
    setRestoring(true)
    try {
      await bulkAction([email.id], 'restore')
      onRefetch()
    } catch {
      /* hook surfaces its own toast; next poll/refetch restores state */
    } finally {
      setRestoring(false)
    }
  }

  // Read/Unread toggle — valid in every folder, so always wired.
  const [readToggling, setReadToggling] = useState(false)
  const handleToggleRead = async (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (readToggling) return
    setReadToggling(true)
    try {
      await toggleRead(email.id, email.is_read)
      onRefetch()
    } catch {
      /* hook surfaces its own toast; next poll/refetch restores state */
    } finally {
      setReadToggling(false)
    }
  }

  // Trailing disposition: Binned shows the caller's restore control;
  // incoming folders get the same "Mark junk" the Inbox has; Sent omits
  // it (self-junk is nonsensical — it would arm a rule on your own
  // address).
  const junkControl =
    trailingControl ??
    (!outgoing && !binned ? (
      <CardJunkControl email={email} onRefetch={onRefetch} compact />
    ) : undefined)

  return (
    <EmailRow
      email={email}
      highlightQuery={highlightQuery?.trim() || undefined}
      category={{
        icon: CatIcon,
        dot: catCfg.dot,
        color: catCfg.hex,
        label: catCfg.label,
        dotIsMuted,
      }}
      isSelected={isSelected}
      isChecked={false}
      // Folder shells don't wire bulk actions — hide the dead checkbox
      // rather than show a click-does-nothing affordance.
      selectable={false}
      onSelect={onSelect}
      onToggleSelect={() => {}}
      onToggleStar={handleToggleStar}
      onContextMenu={onContextMenu}
      // Bin everywhere except the Binned folder; there we show Restore instead.
      onBin={binned ? undefined : () => void handleBin()}
      isBinning={binning}
      onRestore={binned ? () => void handleRestore() : undefined}
      isRestoring={restoring}
      // Archive when not binned/archived; Unarchive (reload arrow) when archived.
      onArchive={binned || email.is_archived ? undefined : () => void handleArchive()}
      isArchiving={archiving}
      onUnarchive={!binned && email.is_archived ? () => void handleUnarchive() : undefined}
      isUnarchiving={unarchiving}
      // Read/Unread toggle is meaningful in every folder.
      onToggleRead={(e) => void handleToggleRead(e)}
      isTogglingRead={readToggling}
      peerName={outgoing ? recipient : undefined}
      peerPrefix={outgoing ? 'To: ' : undefined}
      mailboxChip={
        <MailboxAttributionChip
          email={email}
          currentUserEmail={currentUserEmail}
          currentUserId={currentUserId}
          neutral={rowFramed}
        />
      }
      relationChip={
        <>
          <RelationChip email={email} />
          <ResurfaceChip email={email} />
        </>
      }
      // W5-V12 — the learned counterparty role ("Supplier"/"Client"/...).
      // Self-suppressing (unknown role / already-badged identity → null).
      roleChip={<WorkflowRoleChip email={email} />}
      allocationFrame={allocationFrame}
      jobPillClass={email.job_id ? jobPillClassFor(email.job_id) : undefined}
      allocationChip={
        as ? (
          <AllocationChip
            emailId={email.id}
            summary={as}
            api={kanbanApi}
            orgId={kanbanOrgId}
            onOpenCard={() => {
              const link = buildKanbanCardLink(as)
              if (link) navigate(link)
            }}
            onChanged={onRefetch}
            compact
          />
        ) : null
      }
      aiTag={<CardAiTag state={cardAi.ai} />}
      visibilityBadge={<CardVisibilityBadge visibility={cardAi.visibility} compact />}
      categoryChip={
        showInlineChip ? (
          <CategoryChip
            emailId={email.id}
            category={email.category}
            categoryLocked={email.category_locked}
            pillClass={catCfg.pill}
            icon={CatIcon}
            label={catCfg.label}
            size="sm"
            dotColor={catCfg.hex}
            onChanged={onRefetch}
          />
        ) : null
      }
      junkControl={junkControl}
    />
  )
}

export default FolderEmailRow
